import type { NestedFilter } from '@@partner:graphql/types'

import React, { useState, useCallback } from 'react'
import { useFetchAllBanners } from '@@partner:graphql/query/banners/allBanners.gql'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { generatePath } from 'react-router'

import BannersTable from './grid/BannersTable'
import BannersFilter from './grid/BannersFilter'
import Pagination from 'rsuite/esm/Pagination/Pagination'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { ROUTE_MARKETING_BANNER_EDIT } from '@@partner:config/routes'

export interface BannerGridProps {
  brandSlug: string
}

const defaultBannerFilter: NestedFilter = {
  fields: [ 'id' ],
  op: 'unwrap'
}

export default function BannersGrid ({ brandSlug }: BannerGridProps) {
  const [ filter, setFilter ] = useState<NestedFilter>(defaultBannerFilter)
  const { page, tableSort, handlePageSelect, handleSortColumn } = useGridHelper()
  
  const { data, loading, error } = useFetchAllBanners({
    filter: filter,
    pager: { page },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const generateEditUrl = useCallback(
    (bannerId: string | number) => {
      return generatePath(ROUTE_MARKETING_BANNER_EDIT, {
        brandSlug,
        bannerId: String(bannerId)
      })
    },
    [ brandSlug ]
  )

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки баннеров"
        error={error}
      />
    )
  }

  return (
    <div>
      <BannersFilter onFilterChanged={handleFilterChanged} />

      <BannersTable
        items={data?.allBanners?.edges ?? []}
        loading={loading}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        generateEditUrl={generateEditUrl}
      />

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={data?.allBanners?.pageInfo?.lastPage}
        maxButtons={10}
        onSelect={handlePageSelect}
      />
    </div>
  )
}
